/**
 * Network Status Service
 * Monitors online/offline state and provides network information
 */

import { Network } from '@capacitor/network';
import { isNativePlatform } from '../utils/capacitor';

let networkStatus = { connected: true, connectionType: 'unknown' };
let statusListeners = [];

/**
 * Initialize network monitoring
 */
export const initializeNetworkMonitoring = async () => {
  if (!isNativePlatform()) {
    // Use browser online/offline events for web
    window.addEventListener('online', () => {
      networkStatus = { connected: true, connectionType: 'unknown' };
      notifyListeners(networkStatus);
    });

    window.addEventListener('offline', () => {
      networkStatus = { connected: false, connectionType: 'none' };
      notifyListeners(networkStatus);
    });

    networkStatus = {
      connected: navigator.onLine,
      connectionType: 'unknown'
    };

    return networkStatus;
  }

  try {
    // Get initial status
    networkStatus = await Network.getStatus();

    // Listen for network status changes
    Network.addListener('networkStatusChange', (status) => {
      console.log('Network status changed:', status);
      networkStatus = status;
      notifyListeners(status);
    });

    return networkStatus;
  } catch (error) {
    console.error('Error initializing network monitoring:', error);
    return networkStatus;
  }
};

/**
 * Notify all status listeners
 */
const notifyListeners = (status) => {
  statusListeners.forEach(listener => {
    try {
      listener(status);
    } catch (error) {
      console.error('Error in network status listener:', error);
    }
  });
};

/**
 * Get current network status
 * @returns {Promise<Object>} - { connected: boolean, connectionType: string }
 */
export const getNetworkStatus = async () => {
  if (!isNativePlatform()) {
    return {
      connected: navigator.onLine,
      connectionType: 'unknown'
    };
  }

  try {
    return await Network.getStatus();
  } catch (error) {
    console.error('Error getting network status:', error);
    return networkStatus;
  }
};

/**
 * Check if currently online
 * @returns {boolean}
 */
export const isOnline = () => {
  return networkStatus.connected;
};

/**
 * Check if currently offline
 * @returns {boolean}
 */
export const isOffline = () => {
  return !networkStatus.connected;
};

/**
 * Get connection type
 * @returns {string} - 'wifi', 'cellular', 'none', 'unknown'
 */
export const getConnectionType = () => {
  return networkStatus.connectionType;
};

/**
 * Add network status change listener
 * @param {Function} callback - Called when network status changes
 * @returns {Function} - Unsubscribe function
 */
export const addNetworkListener = (callback) => {
  statusListeners.push(callback);
  
  // Return unsubscribe function
  return () => {
    statusListeners = statusListeners.filter(l => l !== callback);
  };
};

/**
 * Check if has WiFi connection
 * @returns {boolean}
 */
export const hasWiFi = () => {
  return networkStatus.connected && networkStatus.connectionType === 'wifi';
};

/**
 * Check if has cellular connection
 * @returns {boolean}
 */
export const hasCellular = () => {
  return networkStatus.connected && networkStatus.connectionType === 'cellular';
};

/**
 * Wait for network connection
 * @param {number} timeout - Max time to wait in ms (default 30000)
 * @returns {Promise<boolean>} - True if connected, false if timeout
 */
export const waitForConnection = (timeout = 30000) => {
  return new Promise((resolve) => {
    if (isOnline()) {
      resolve(true);
      return;
    }

    let timeoutId;
    const unsubscribe = addNetworkListener((status) => {
      if (status.connected) {
        clearTimeout(timeoutId);
        unsubscribe();
        resolve(true);
      }
    });

    timeoutId = setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, timeout);
  });
};
